import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { formatApiError } from "../lib/api";
import { Phone, Mail } from "lucide-react";

export default function LoginPage() {
  const { login, user, loading, unreachable } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // One-shot Swedish notice when the user was auto-logged-out mid-session
  // (axios interceptor couldn't refresh the session).
  useEffect(() => {
    try {
      if (sessionStorage.getItem("vfm_session_lost") === "1") {
        sessionStorage.removeItem("vfm_session_lost");
        setError("Din session har gått ut. Logga in igen för att fortsätta.");
      }
    } catch { /* storage disabled */ }
  }, []);

  if (loading && !unreachable) return <div className="min-h-screen flex items-center justify-center text-slate-400">Laddar…</div>;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username.trim().toLowerCase(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "linear-gradient(180deg,#F0F6FC 0%,#ffffff 100%)" }}
    >
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
        <img src="/valfrimobil-logo.webp" alt="ValfriMobil"
             className="h-10 w-auto" data-testid="login-logo" />
        <span className="text-xs text-slate-400 hidden sm:inline">
          Internt verkstadssystem
        </span>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 pt-10 sm:pt-16">
        <div
          data-testid="login-card"
          className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-slate-100 p-8 sm:p-10 fade-in-up"
        >
          <h1
            className="text-3xl sm:text-4xl font-bold display-tight mb-1.5"
            style={{ fontFamily: "Manrope, sans-serif", color: "#0F172A" }}
          >
            Logga <span style={{ color: "#3B9EDA" }}>in</span>
          </h1>
          <p className="text-slate-500 text-sm mb-7">
            Ange ditt användarnamn och PIN-kod (eller tidigare lösenord) för att komma åt ärendesystemet.
          </p>

          <form onSubmit={submit} className="space-y-4" autoComplete="on">
            <div className="space-y-1.5">
              <Label htmlFor="username">Användarnamn</Label>
              <Input
                id="username"
                data-testid="input-username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="t.ex. semaan"
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">PIN-kod eller lösenord</Label>
              <Input
                id="password"
                data-testid="input-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="PIN-kod eller tidigare lösenord"
                maxLength={128}
                required
              />
            </div>

            <div className="flex items-center justify-end pt-1">
              <button
                type="button"
                onClick={() => navigate("/glomt-losenord")}
                className="text-sm text-[#3B9EDA] hover:underline font-medium"
              >
                Glömt PIN-kod?
              </button>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="button-login"
              disabled={busy || !username || !password}
              className="press-feedback w-full rounded-full py-6 text-white text-base font-semibold min-h-[52px]"
              style={{ backgroundColor: "#3B9EDA" }}
            >
              {busy ? "Loggar in…" : "Logga in"}
            </Button>
          </form>

          <p data-testid="login-session-info" className="text-center text-xs text-slate-500 mt-6">
            Du förblir inloggad tills du loggar ut.
          </p>
        </div>
      </main>

      <footer className="py-6 text-center text-sm text-slate-500 border-t border-slate-100">
        <p className="mb-2">© 2026 Valfrimobil Service</p>
        <div className="flex items-center justify-center gap-4 flex-wrap text-xs">
          <a href="tel:0720080008" className="inline-flex items-center gap-1 hover:text-[#3B9EDA]">
            <Phone className="h-3.5 w-3.5" /> 0720080008
          </a>
          <span className="text-slate-300">|</span>
          <a href="mailto:molndal@valfrimobil.se" className="inline-flex items-center gap-1 hover:text-[#3B9EDA]">
            <Mail className="h-3.5 w-3.5" /> molndal@valfrimobil.se
          </a>
        </div>
      </footer>
    </div>
  );
}
